import { useCallback, useEffect, useState } from "react";
import { Lock, Plus, Unlock } from "lucide-react";
import { fetchPayPeriods, createPayPeriod, togglePayPeriodLock } from "../tipsApi";
import type { PayPeriod } from "../types";

function nextMonday(after?: string): string {
  const d = after ? new Date(after + "T00:00:00") : new Date();
  if (after) d.setDate(d.getDate() + 1);
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
  return iso(d);
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return iso(d);
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtDate(s: string): string {
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function PayPeriodsView() {
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setPeriods(await fetchPayPeriods());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate() {
    setCreating(true);
    setError(null);
    try {
      const lastEnd = periods.length > 0 ? periods[0].end_date : undefined;
      const start = nextMonday(lastEnd);
      const end = addDays(start, 13);
      await createPayPeriod(start, end);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function handleToggleLock(pp: PayPeriod) {
    setError(null);
    try {
      await togglePayPeriodLock(pp.id, !pp.is_locked);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Pay Periods</h2>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-1.5 rounded-lg bg-tribal-red px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> {creating ? "Creating…" : "Create Next Period"}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</div>
      )}

      {loading ? (
        <p className="text-sm text-tribal-muted">Loading…</p>
      ) : periods.length === 0 ? (
        <p className="text-sm text-tribal-muted">No pay periods yet. Create the first one above.</p>
      ) : (
        <div className="space-y-2">
          {periods.map((pp) => (
            <div
              key={pp.id}
              className="flex items-center justify-between rounded-lg border border-tribal-border bg-tribal-surface px-4 py-3"
            >
              <div>
                <span className="font-medium">{fmtDate(pp.start_date)}</span>
                <span className="mx-2 text-tribal-muted">—</span>
                <span className="font-medium">{fmtDate(pp.end_date)}</span>
                {pp.notes && (
                  <span className="ml-3 text-xs text-tribal-muted">{pp.notes}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleToggleLock(pp)}
                title={pp.is_locked ? "Unlock period" : "Lock period"}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  pp.is_locked
                    ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
                    : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                }`}
              >
                {pp.is_locked ? (
                  <><Lock className="h-3.5 w-3.5" /> Locked</>
                ) : (
                  <><Unlock className="h-3.5 w-3.5" /> Open</>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
